<template>
  <div>
    <div class="flex items-center justify-between mb-2">
      <label class="text-sm font-medium text-gray-800">Audience & Project</label>
      <div class="flex items-center gap-2">
        <button
          v-if="store.projectOverriddenFields.value.length > 0"
          @click="store.clearProjectOverrides()"
          class="text-sm text-orange-600 hover:text-orange-800"
        >Reset all</button>
        <button
          v-if="store.projectOverriddenFields.value.length > 0"
          @click="store.saveBackToProject()"
          class="text-sm bg-orange-500 text-white rounded px-2 py-0.5 hover:bg-orange-600"
        >Save to Project</button>
      </div>
    </div>

    <div class="space-y-3">
      <div v-for="field in projectFields" :key="field.key">
        <label class="text-sm font-medium text-gray-800 mb-0.5 flex items-center gap-1">
          {{ field.label }}
          <span v-if="isOverridden(field.key)" class="w-1.5 h-1.5 bg-orange-400 rounded-full" title="Overridden"></span>
        </label>
        <p v-if="field.hint" class="text-sm text-gray-600 mb-0.5">{{ field.hint }}</p>
        <textarea
          v-if="field.multiline"
          ref="autoTextareas"
          :value="effective(field.key)"
          @input="onUpdate(field.key, $event.target.value); autoResize($event.target)"
          :placeholder="field.placeholder"
          rows="1"
          class="w-full text-sm text-gray-900 border rounded px-2 py-1.5 focus:outline-none resize-none overflow-hidden"
          :class="isOverridden(field.key)
            ? 'border-orange-300 bg-orange-50/50 focus:border-orange-400'
            : 'border-gray-300 focus:border-teal-500'"
        ></textarea>
        <input
          v-else
          type="text"
          :value="effective(field.key)"
          @input="onUpdate(field.key, $event.target.value)"
          :placeholder="field.placeholder"
          class="w-full text-sm text-gray-900 border rounded px-2 py-1.5 focus:outline-none"
          :class="isOverridden(field.key)
            ? 'border-orange-300 bg-orange-50/50 focus:border-orange-400'
            : 'border-gray-300 focus:border-teal-500'"
        />
      </div>
    </div>

    <!-- Source files -->
    <div class="mt-4">
      <div class="flex items-center justify-between mb-1">
        <label class="text-sm font-medium text-gray-800">Source files</label>
        <button
          @click="triggerFileUpload"
          :disabled="uploading"
          class="text-sm text-teal-700 hover:text-teal-900 font-medium disabled:opacity-50"
        >
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".txt,.md,.csv,.json"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />
      </div>
      <div v-if="files.length" class="space-y-1">
        <div
          v-for="file in files"
          :key="file.id"
          class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-sm"
        >
          <span class="text-gray-800 truncate">{{ file.filename }}</span>
          <button
            @click="deleteFile(file.id)"
            class="text-gray-400 hover:text-red-600 ml-2 flex-shrink-0"
            title="Remove file"
          >&times;</button>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500 italic">No files uploaded</p>
    </div>

    <!-- Project Knowledge (inline CRUD) -->
    <div class="mt-4">
      <div class="flex items-center gap-2 mb-1">
        <div class="flex-1 border-t border-gray-200"></div>
        <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">Project Knowledge</span>
        <button
          @click="addKI"
          class="text-xs text-teal-700 hover:text-teal-900 font-medium"
        >+ Add</button>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>
      <div v-if="knowledgeItems.length" class="space-y-2">
        <div
          v-for="(ki, idx) in knowledgeItems"
          :key="ki.id || ki._tempId"
          class="flex items-start bg-gray-50 border border-gray-200 rounded"
        >
          <textarea
            v-model="ki.content"
            @blur="saveKI(ki)"
            @input="autoResize($event.target)"
            ref="kiTextareas"
            rows="1"
            placeholder="Paste reference material..."
            class="flex-1 text-sm text-gray-900 border-0 bg-transparent rounded-l px-3 py-2 focus:outline-none resize-none overflow-hidden"
          ></textarea>
          <button
            @click="deleteKI(ki, idx)"
            class="px-2 py-2 text-gray-400 hover:text-red-500 flex-shrink-0"
            title="Delete"
          >&times;</button>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500 italic">No knowledge items yet</p>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, watch, nextTick, onMounted } from 'vue'
import { useApi } from '../../composables/useApi'

const store = inject('workbench')
const api = useApi()

const fileInput = ref(null)
const files = ref([])
const uploading = ref(false)
const autoTextareas = ref([])
const kiTextareas = ref([])

const projectFields = [
  { key: 'audience', label: 'Audience', multiline: true, placeholder: 'Mid-career marketing managers at B2B SaaS companies. Comfortable with analytics but not deeply technical. Read content to find actionable tactics they can implement this week.', hint: 'Who reads this. Be specific \u2014 demographics, role, interests. 1-3 sentences.' },
  { key: 'audience_expertise', label: 'Expertise level', multiline: true, placeholder: 'Intermediate \u2014 they know the fundamentals but want advanced tactics and frameworks. No need to explain basic terms like CAC or churn.', hint: 'How much they already know. Affects depth and jargon. 1 sentence.' },
  { key: 'objective', label: 'Objective', multiline: true, placeholder: 'Educate readers on a specific technique they can apply immediately, and position our brand as the trusted expert.', hint: 'What the content should achieve \u2014 educate, persuade, entertain. 1-2 sentences.' },
  { key: 'language', label: 'Language', multiline: false, placeholder: 'English', hint: 'Output language. One word.' },
]

function effective(key) {
  return store.effectiveProject.value?.[key] || ''
}

function isOverridden(key) {
  return store.projectOverriddenFields.value.includes(key)
}

function onUpdate(key, value) {
  store.setProjectOverride(key, value)
}

// --- Auto-resize textareas ---
function autoResize(el) {
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

function resizeAllTextareas() {
  nextTick(() => {
    const els = autoTextareas.value || []
    for (const el of els) {
      if (el) autoResize(el)
    }
    const kiEls = kiTextareas.value || []
    for (const el of kiEls) {
      if (el) autoResize(el)
    }
  })
}

onMounted(resizeAllTextareas)

// Resize when project changes (new values loaded)
watch(() => store.effectiveProject.value, resizeAllTextareas, { deep: true })

// --- Source files ---
watch(() => store.currentContent.value, (content) => {
  if (content) {
    files.value = content.content_sources || []
  }
}, { immediate: true })

function triggerFileUpload() {
  fileInput.value?.click()
}

async function handleFileSelect(event) {
  const selectedFiles = event.target.files
  if (!selectedFiles?.length) return

  uploading.value = true
  try {
    let contentId = store.currentContent.value?.id
    if (!contentId) {
      const form = store.inputForm.value
      await store.saveContent({
        brief: form?.brief,
        title: form?.title,
        format: form?.format || 'article',
        status: 'draft',
        author_id: store.currentAuthor.value?.id,
      })
      contentId = store.currentContent.value?.id
    }
    if (!contentId) return

    for (const file of selectedFiles) {
      const source = await store.uploadSourceFile(contentId, file)
      if (source) files.value.push(source)
    }
  } finally {
    uploading.value = false
    if (fileInput.value) fileInput.value.value = ''
  }
}

async function deleteFile(sourceId) {
  const contentId = store.currentContent.value?.id
  if (!contentId) return

  await store.deleteSourceFile(contentId, sourceId)
  files.value = files.value.filter(f => f.id !== sourceId)
}

// --- Knowledge items (inline CRUD) ---
const knowledgeItems = ref([])
let tempIdCounter = 0

watch(() => store.currentProject.value, (project) => {
  if (project) {
    knowledgeItems.value = (project.knowledge_items || []).map(ki => ({ ...ki }))
  } else {
    knowledgeItems.value = []
  }
  nextTick(resizeAllTextareas)
}, { immediate: true })

function addKI() {
  knowledgeItems.value.push({ _tempId: ++tempIdCounter, content: '', _new: true })
}

async function saveKI(ki) {
  const projectId = store.currentProject.value?.id
  if (!projectId || !ki.content?.trim()) return
  try {
    if (ki._new || !ki.id) {
      const created = await api.post(`/api/projects/${projectId}/knowledge_items`, {
        knowledge_item: { title: '\u2014', content: ki.content }
      })
      ki.id = created.id
      ki._new = false
      delete ki._tempId
    } else {
      await api.patch(`/api/projects/${projectId}/knowledge_items/${ki.id}`, {
        knowledge_item: { content: ki.content }
      })
    }
    syncKIToStore()
  } catch (e) {
    console.error('Failed to save knowledge item', e)
  }
}

async function deleteKI(ki, idx) {
  const projectId = store.currentProject.value?.id
  if (ki.id && projectId) {
    try { await api.delete(`/api/projects/${projectId}/knowledge_items/${ki.id}`) } catch (e) { /* ignore */ }
  }
  knowledgeItems.value.splice(idx, 1)
  syncKIToStore()
}

function syncKIToStore() {
  if (store.currentProject.value) {
    store.currentProject.value.knowledge_items = knowledgeItems.value
      .filter(ki => ki.id)
      .map(ki => ({ id: ki.id, content: ki.content }))
  }
}
</script>
